import {
  Alert,
  Button,
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { memo, useCallback, useMemo } from "react";
import { Search } from "@mui/icons-material";

import { useOrganizationContext } from "../../../contexts/OrganizationContext";
import { useInterventionsContext } from "../../../contexts/InterventionsContext";

import { useQuestionnaireManagement } from "src-next/modules/questionnaire-builder/questionnaire-management/hooks";
import type { QuestionnairePayload } from "src-next/modules/questionnaire-builder/form-builder/types";
import { oids } from "@/configs/Settings";
import { isQuestionnaireSelectableForClinicalUse } from "@/utils/questionnaireClinicalSelection";

/**
 * Aba de Questionários na consulta (intervenções).
 *
 * Decisão:
 * - Reutiliza o mesmo modelo implementado em protocolos:
 *   - lista real via `useQuestionnaireManagement({ organizationId })`
 *   - seleção adiciona/remove itens `type: "questionnaire"` em `interventionsList`
 */
const QuestionnairesTabPanel = memo(() => {
  const { organization } = useOrganizationContext();
  const { selectedInterventions, functions, setSelectProtocol } =
    useInterventionsContext();

  const { questionnaires, isLoading, errorMessage } =
    useQuestionnaireManagement({
      organizationId: `${organization?.id}`,
    });

  const selectedIds = useMemo(() => {
    const items = selectedInterventions?.questionnaires || [];
    return new Set(
      items
        .map((item: any) => item?.questionnaireId)
        .filter((id: any) => typeof id === "string" && id.length > 0),
    );
  }, [selectedInterventions?.questionnaires]);

  const filteredQuestionnaires = useMemo(
    () =>
      questionnaires.filter(
        (q) =>
          q.usageType === "postConsultation" &&
          isQuestionnaireSelectableForClinicalUse(q),
      ),
    [questionnaires],
  );

  const toggleSelection = useCallback(
    (questionnaire: QuestionnairePayload) => {
      const questionnaireId = questionnaire.id;
      if (!questionnaireId) return;
      if (!isQuestionnaireSelectableForClinicalUse(questionnaire)) return;

      const currentAll = selectedInterventions?.all || [];
      const existingIndex = currentAll.findIndex(
        (item: any) =>
          item?.type === "questionnaire" &&
          item?.questionnaireId === questionnaireId,
      );

      if (existingIndex !== -1) {
        functions.handleRemove(existingIndex);
        return;
      }

      functions.handleAdd({
        type: "questionnaire",
        questionnaireId,
        title: questionnaire.title,
        name: questionnaire.title,
        // Mantém compatibilidade com dialogs que usam `${type}-${name}-${label}`
        label: questionnaireId,
        usageType: questionnaire.usageType,
      });
    },
    [functions, selectedInterventions?.all],
  );

  return (
    <Stack width="100%" maxHeight="60vh" overflow="auto" spacing={1.5}>
      <Button
        sx={{ width: "fit-content", textTransform: "none" }}
        startIcon={<Search />}
        onClick={() => setSelectProtocol(true)}
      >
        Selecionar protocolo salvo
      </Button>
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Questionários de pós-consulta
        </Typography>

        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
      </Box>

      <List
        dense
        disablePadding
        sx={{
          borderRadius: 1,
          borderColor: "divider",
          maxHeight: 320,
          overflow: "auto",
        }}
      >
        {isLoading ? (
          <ListItem>
            <ListItemText
              primary="Carregando questionários..."
              secondary="Aguarde alguns segundos."
            />
          </ListItem>
        ) : filteredQuestionnaires.length === 0 ? (
          <ListItem>
            <ListItemText
              primary="Nenhum questionário encontrado."
              secondary="Crie um questionário ou ajuste os filtros."
            />
          </ListItem>
        ) : (
          filteredQuestionnaires.map((questionnaire) => {
            const questionnaireId = questionnaire.id;
            const isSelected = questionnaireId
              ? selectedIds.has(questionnaireId)
              : false;

            return (
              <ListItem
                key={questionnaireId || questionnaire.title}
                secondaryAction={
                  <Checkbox
                    edge="end"
                    checked={isSelected}
                    onChange={() => toggleSelection(questionnaire)}
                  />
                }
              >
                <ListItemButton onClick={() => toggleSelection(questionnaire)}>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={500}>
                        {questionnaire.title}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        Pós-consulta
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            );
          })
        )}
      </List>
    </Stack>
  );
});

QuestionnairesTabPanel.displayName = "QuestionnairesTabPanel";

export default QuestionnairesTabPanel;
